import { useRef, useState } from "react";
import { leviathanRules } from "@personnel/rules-leviathan";
import type { LeviathanCaptain, LeviathanDerived } from "@personnel/rules-leviathan";
import { slugify } from "../../../lib/download.js";
import { CaptainCard } from "./CaptainCard.js";
import { downloadBlob, svgToPngBlob } from "./exportCard.js";

interface CardPanelProps {
  captain: LeviathanCaptain;
  derived: LeviathanDerived;
}

/** 750px is the card at 300dpi; 1500 doubles it for print. */
const SIZES = [
  { label: "Standard", width: 750 },
  { label: "Large", width: 1500 },
];

export function CardPanel({ captain, derived }: CardPanelProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async (width: number) => {
    if (svgRef.current === null) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await svgToPngBlob(svgRef.current, { width });
      downloadBlob(`${slugify(leviathanRules.describe(captain), "captain")}-card.png`, blob);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Export failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-hidden rounded-xl border border-void-800">
        <CaptainCard ref={svgRef} captain={captain} derived={derived} />
      </div>

      <div className="flex gap-2">
        {SIZES.map(({ label, width }) => (
          <button
            key={label}
            type="button"
            onClick={() => void handleExport(width)}
            disabled={busy}
            className="flex-1 rounded-md border border-void-800 px-3 py-2 text-xs font-medium text-void-400 transition-colors hover:border-leviathan-600 hover:text-leviathan-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Rendering…" : `PNG ${label}`}
          </button>
        ))}
      </div>

      {error !== null && (
        <p className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}
